import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export type ImageLightboxFit =
  /** Scale the image down until all of it fits the window. */
  | "contain"
  /** Show the image at its own pixel size and let the window scroll. For a
   * screenshot whose whole point is being read: scaled to fit, a tall capture
   * shrinks back to the same unreadable size it was on the page. */
  | "natural";

export type ImageLightboxProps = {
  src: string;
  alt: string;
  /** Names the dialog for assistive tech, e.g. "Today page full-size screenshot". */
  label: string;
  fit: ImageLightboxFit;
  onClose: () => void;
  /** Shown under the image, inside the dialog: a line about it, a link to
   *  download it. A click here does not close the lightbox. */
  children?: ReactNode;
};

/**
 * A full-size view of one image over the page.
 *
 * Portaled to <body> so it escapes the page's stacking context and sits above
 * the sticky navbar. Closes on Escape, on the Close button, and on a click
 * outside the image; a click on the image itself is left alone so a reader
 * scrolling a large one doesn't lose it.
 */
export function ImageLightbox({
  src,
  alt,
  label,
  fit,
  onClose,
  children,
}: ImageLightboxProps) {
  // Callers pass an inline arrow, which is a new function every render. Read
  // it through a ref so the listener and the scroll lock are set up once for
  // the lightbox's life instead of torn down and redone on each render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    // The page underneath must not scroll while the image is being read —
    // in natural mode the wheel belongs to the lightbox.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div
      className={`tb-lightbox tb-lightbox--${fit}`}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <button
        type="button"
        className="tb-lightbox-close"
        onClick={onClose}
        aria-label="Close full-size image"
      >
        Close
      </button>
      <div className="tb-lightbox-content" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} className="tb-lightbox-image" />
        {children ? (
          <div className="tb-lightbox-caption">{children}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
